// Ageing and last-run rules for the queue, as pure functions so they are tested
// without rendering anything.
//
// The colours are informational. Nothing auto-removes an old row; amber and red
// only say "someone should look at this".

import { parseServerTime } from "./api";

export const AMBER_DAYS = 3;
export const RED_DAYS = 7;

// A run still `running` after this long has almost certainly died with the
// laptop lid, and its leases are holding claims nobody is working.
export const STALE_RUN_HOURS = 6;

// '' | 'amber' | 'red' for a queue row's age in whole days.
export function ageTone(days) {
  const n = Number(days);
  if (!Number.isFinite(n)) return "";
  if (n >= RED_DAYS) return "red";
  if (n >= AMBER_DAYS) return "amber";
  return "";
}

export function ageLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1) return "today";
  return `${Math.floor(n)}d`;
}

// "5 minutes ago", "3 hours ago", "2 days ago". Coarse on purpose: the banner is
// read at a glance, and a clock time next to it would invite timezone questions.
export function relativeTime(value, now = new Date()) {
  const when = value instanceof Date ? value : parseServerTime(value);
  if (!when) return "at an unknown time";
  const seconds = Math.round((now.getTime() - when.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// What the last-run banner says and how loudly.
//   grey  — never run, or a run that ended normally
//   amber — the last run asked for a human
//   red   — a run failed, was abandoned, or has been `running` past STALE_RUN_HOURS
export function lastRunState(run, now = new Date()) {
  if (!run) {
    return {
      tone: "grey",
      text: "Cowork skill has never run for this practice.",
    };
  }
  const started = parseServerTime(run.started_at);
  const submitted = Number(run.submitted_count) || 0;
  const failed = Number(run.failed_count) || 0;
  const counts = `${submitted} submitted · ${failed} failed`;
  const when = relativeTime(run.finished_at || run.started_at, now);

  if (run.status === "running") {
    const hours = started ? (now.getTime() - started.getTime()) / 3600000 : 0;
    if (hours > STALE_RUN_HOURS) {
      return {
        tone: "red",
        text: `Cowork skill started ${relativeTime(started, now)} and has not finished · ${counts}`,
        detail:
          "Its leases return to the queue when they expire. Check the machine that ran it.",
      };
    }
    return {
      tone: "grey",
      text: `Cowork skill is running (started ${relativeTime(started, now)}) · ${counts}`,
    };
  }
  if (run.status === "abandoned" || run.status === "failed") {
    return {
      tone: "red",
      text: `Cowork skill last ran ${when} and ${run.status === "failed" ? "failed" : "was abandoned"} · ${counts}`,
    };
  }
  if (run.needs_human) {
    return {
      tone: "amber",
      text: `Cowork skill last ran ${when} · ${counts} · it needs a person`,
    };
  }
  return { tone: "grey", text: `Cowork skill last ran ${when} · ${counts}` };
}
